import { RotateClockwise } from "@procore/core-icons";
import { Box, Button, spacing } from "@procore/core-react";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    offlineActions,
    selectIncrementalStorageLimit,
    selectIncrementalStorageSize,
    selectOfflineAction,
    selectOfflineScenes,
    useOfflineSceneDirName,
    useOfflineSceneKey,
} from "features/offline";
import { formatFileSizeMetric } from "utils/misc";

import { IdRow, Section } from "./common";

/** Clears offline data through `useHandleOffline`, which also releases the offline index and re-arms incremental caching. */
export function OfflineSection() {
    const {
        state: { offlineWorkerState },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const action = useAppSelector(selectOfflineAction)?.action;
    const scenes = useAppSelector(selectOfflineScenes);
    const incrementalSize = useAppSelector(selectIncrementalStorageSize);
    const incrementalLimit = useAppSelector(selectIncrementalStorageLimit);
    const sceneKey = useOfflineSceneKey();
    const scene = scenes[useOfflineSceneDirName()];
    const busy = action === "delete" || action === "deleteAll";
    const [sizes, setSizes] = useState<{ manifest: number; files: number } | null>();
    const [sizeRefresh, setSizeRefresh] = useState(0);
    const sceneStatus = scene?.view.kind;

    // Not `scene.size`: it is the manifest's total, which an incremental scene leaves at 0.
    useEffect(() => {
        setSizes(undefined);
        const stored = offlineWorkerState?.getScene(sceneKey);
        if (!stored) {
            setSizes(null);
            return;
        }
        let cancelled = false;
        Promise.all([stored.getState(), stored.getUsedSize()]).then(
            ([state, used]) => {
                // Only a synced scene has a manifest's worth of files.
                const manifest = state.kind === "synced" ? state.total.bytes : 0;
                if (!cancelled) {
                    setSizes({ manifest, files: used - manifest });
                }
            },
            (e: unknown) => {
                console.error("Failed to read offline scene size", e);
                if (!cancelled) {
                    setSizes(null);
                }
            },
        );
        return () => {
            cancelled = true;
        };
    }, [offlineWorkerState, sceneKey, sceneStatus, sizeRefresh]);

    return (
        <Section title="Offline">
            <Box>
                <IdRow
                    label="This scene"
                    value={sceneStatus}
                    placeholder={offlineWorkerState ? "Not stored" : "Offline unsupported"}
                />
                <IdRow
                    label="Scene size"
                    value={
                        sizes
                            ? `${formatFileSizeMetric(sizes.files)} on disk, ${formatFileSizeMetric(sizes.manifest)} in manifest`
                            : undefined
                    }
                    placeholder={sizes === undefined ? "Calculating…" : undefined}
                >
                    <Button
                        icon={<RotateClockwise />}
                        variant="tertiary"
                        size="sm"
                        aria-label="Recalculate scene size"
                        title="Recalculate"
                        disabled={!scene || sizes === undefined}
                        onClick={() => setSizeRefresh((n) => n + 1)}
                    />
                </IdRow>
                <IdRow
                    label="Incremental"
                    value={
                        incrementalSize === null
                            ? undefined
                            : `${formatFileSizeMetric(incrementalSize)} / ${formatFileSizeMetric(incrementalLimit ?? undefined)}`
                    }
                />
            </Box>
            <Box style={{ display: "flex", gap: spacing.sm }}>
                <Button
                    variant="secondary"
                    size="sm"
                    disabled={!offlineWorkerState || !scene || busy}
                    loading={action === "delete"}
                    onClick={() => dispatch(offlineActions.setAction({ action: "delete" }))}
                >
                    Clear scene data
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    disabled={!offlineWorkerState || busy}
                    loading={action === "deleteAll"}
                    onClick={() =>
                        window.confirm("Delete offline data for every scene on this device?") &&
                        dispatch(offlineActions.setAction({ action: "deleteAll" }))
                    }
                >
                    Clear all data
                </Button>
            </Box>
        </Section>
    );
}
